import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Search, Menu, X, User, LogOut, ChevronDown } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import masshappyIcon from "@/assets/masshappy-icon.png";
import LanguageSelector from "@/components/LanguageSelector";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Header = () => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // Sesion y rol vienen del hook useAuth, unica fuente de verdad
  const { user, loading, isAdmin, isTherapist, roles, displayName: userName } = useAuth();
  const hasMultipleRoles = roles.length > 1;
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const isResidency = location.pathname === "/retiro";
  
  const isTransparent = isResidency && !scrolled && !isMenuOpen;

  useEffect(() => {
    if (!isResidency) return;
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isResidency]);

  const handleSignOut = async () => {
    try {
      const {
        error
      } = await supabase.auth.signOut();
      if (error) {
        toast({
          title: t("auth.errorTitle"),
          description: t("auth.signOutError"),
          variant: "destructive"
        });
      } else {
        toast({
          title: t("auth.signedOutTitle"),
          description: t("auth.signedOutText")
        });
        navigate("/");
      }
    } catch (error) {
      toast({
        title: t("auth.errorTitle"),
        description: t("auth.signOutError"),
        variant: "destructive"
      });
    }
  };
  return <>
    {!isResidency && <div aria-hidden="true" className="h-[72px]" />}
    <header className={`fixed top-0 z-50 w-full transition-all duration-300 ${isTransparent ? "bg-transparent border-transparent shadow-none" : "bg-background/95 backdrop-blur-sm border-b border-border shadow-soft"}`}>
      <div className="container mx-auto px-4 py-4 max-w-7xl">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img src={masshappyIcon} alt="Masshappy icon" className={`h-8 w-auto transition-all duration-300 ${isTransparent ? "brightness-0 invert" : ""}`} width={28} height={32} />
            <span className={`font-poppins text-xl font-medium transition-colors duration-300 ${isTransparent ? "text-white" : ""}`} style={isTransparent ? undefined : { color: 'hsl(7, 65%, 55%)' }}>masshappy</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            <a href="/#app" className={`${isTransparent ? "text-white/90 hover:text-white" : "text-foreground hover:text-primary"} transition-smooth`}>
              {t("nav.theApp")}
            </a>
            <Link to="/retiro" className={`${isTransparent ? "text-white/90 hover:text-white" : "text-foreground hover:text-primary"} transition-smooth`}>
              {t("nav.residency")}
            </Link>
            <a href="/terapeutas" className={`${isTransparent ? "text-white/90 hover:text-white" : "text-foreground hover:text-primary"} transition-smooth`}>
              {t("nav.therapists")}
            </a>

          </nav>

          {/* Desktop Auth Buttons */}
          <div className="hidden lg:flex items-center space-x-3">
            <LanguageSelector transparent={isTransparent} />
            {loading ? <div className="w-20 h-9 bg-muted animate-pulse rounded"></div> : user ? <>
                {userName && <span className={`text-sm ${isTransparent ? 'text-white/80' : 'text-muted-foreground'}`}>
                  {t('common.hello')}, {userName}
                </span>}
                {hasMultipleRoles ? <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className={isTransparent ? 'border-white/30 text-white hover:bg-white/20 hover:text-white' : ''}>
                      Mi panel
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-background z-50">
                    {isAdmin && <DropdownMenuItem asChild>
                      <Link to="/admin">Administración</Link>
                    </DropdownMenuItem>}
                    {isTherapist && <DropdownMenuItem asChild>
                      <Link to="/negocio">Mi negocio</Link>
                    </DropdownMenuItem>}
                    <DropdownMenuItem asChild>
                      <a href="https://app.masshappy.com" target="_blank" rel="noopener noreferrer">Abrir la app</a>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu> : <Button variant="outline" size="sm" className={isTransparent ? 'border-white/30 text-white hover:bg-white/20 hover:text-white' : ''} asChild>
                  <a href="https://app.masshappy.com" target="_blank" rel="noopener noreferrer">Abrir la app</a>
                </Button>}

                <Button variant="ghost" className={isTransparent ? 'text-white/90 hover:text-white hover:bg-white/20' : ''} onClick={handleSignOut}>
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('nav.logout')}
                </Button>
              </> : <>
                <Button variant="ghost" className={isTransparent ? 'text-white/90 hover:text-white hover:bg-white/20' : ''} asChild>
                  <Link to="/acceso"><User className="w-4 h-4 mr-2" />{t('nav.login')}</Link>
                </Button>
                <Button variant={isTransparent ? 'secondary' : 'default'} className={isTransparent ? 'bg-white text-foreground font-semibold hover:bg-white/90' : ''} asChild>
                  <a href="https://app.masshappy.com" target="_blank" rel="noopener noreferrer">{t("nav.openApp")}</a>
                </Button>

              </>}
          </div>

          {/* Mobile Menu Button */}
          <Button variant="ghost" size="icon" aria-label={isMenuOpen ? t("nav.closeMenu") : t("nav.openMenu")} aria-expanded={isMenuOpen} aria-controls="mobile-menu" className={`lg:hidden ${isTransparent ? "text-white hover:bg-white/10" : ""}`} onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X /> : <Menu />}
          </Button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && <div id="mobile-menu" className="lg:hidden mt-4 pb-4 border-t border-border pt-4">
            <nav className="flex flex-col space-y-3">
              <a href="/#app" className="text-foreground hover:text-primary transition-smooth py-2" onClick={() => setIsMenuOpen(false)}>
                {t("nav.theApp")}
              </a>
              <Link to="/retiro" className="text-foreground hover:text-primary transition-smooth py-2" onClick={() => setIsMenuOpen(false)}>
                {t("nav.residency")}
              </Link>
              <a href="/terapeutas" className="text-foreground hover:text-primary transition-smooth py-2" onClick={() => setIsMenuOpen(false)}>
                {t("nav.therapists")}
              </a>

              <div className="flex flex-col space-y-2 pt-3">
                <LanguageSelector transparent={isTransparent} />
                {loading ? <div className="w-full h-9 bg-muted animate-pulse rounded"></div> : user ? <>
                    {userName && <div className="py-2 text-sm text-muted-foreground">
                      {t('common.hello')}, {userName}
                    </div>}
                    {hasMultipleRoles ? <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="w-full justify-between">
                          Mi panel
                          <ChevronDown className="w-4 h-4 ml-2" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="bg-background z-50 w-[calc(100vw-3rem)] max-w-xs">
                        {isAdmin && <DropdownMenuItem asChild>
                          <Link to="/admin" onClick={() => setIsMenuOpen(false)}>Administración</Link>
                        </DropdownMenuItem>}
                        {isTherapist && <DropdownMenuItem asChild>
                          <Link to="/negocio" onClick={() => setIsMenuOpen(false)}>Mi negocio</Link>
                        </DropdownMenuItem>}
                        <DropdownMenuItem asChild>
                          <a href="https://app.masshappy.com" target="_blank" rel="noopener noreferrer" onClick={() => setIsMenuOpen(false)}>Abrir la app</a>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu> : <Button variant="outline" className="w-full" asChild>
                      <a href="https://app.masshappy.com" target="_blank" rel="noopener noreferrer" onClick={() => setIsMenuOpen(false)}>Abrir la app</a>
                    </Button>}
                    <Button variant="ghost" onClick={() => {
                handleSignOut();
                setIsMenuOpen(false);
              }}>
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('nav.logout')}
                    </Button>
                  </> : <>
                    <Button variant="default" size="lg" className="w-full font-semibold" asChild>
                      <a href="https://app.masshappy.com" target="_blank" rel="noopener noreferrer" onClick={() => setIsMenuOpen(false)}>
                        {t("nav.openApp")}
                      </a>
                    </Button>
                    <Button variant="outline" size="lg" className="w-full" asChild>
                      <Link to="/acceso" onClick={() => setIsMenuOpen(false)}>{t('nav.login')}</Link>
                    </Button>

                  </>}
              </div>
            </nav>
          </div>}
      </div>
    </header>
  </>;
};
export default Header;